import React, { useState, useEffect, useRef } from "react";
import "./UserGuideModal.css";

const SECTIONS = [
  { id: "intro", title: "Introduction", num: "1" },
  { id: "dashboard", title: "Home Dashboard", num: "2" },
  { id: "new-assessment", title: "New Assessment", num: "3" },
  { id: "completing", title: "Scoring & Evidence", num: "4" },
  { id: "history", title: "Assessment History", num: "5" },
  { id: "admin", title: "Admin Panel", num: "6" },
  { id: "reports", title: "Report Builder", num: "7" },
  { id: "best-practices", title: "Best Practices", num: "8" },
];

export default function UserGuideModal({ isOpen, onClose }) {
  const [dontRemind, setDontRemind] = useState(
    () => localStorage.getItem("ahc_hide_user_guide") === "true"
  );
  const [activeSection, setActiveSection] = useState("intro");
  const [lightboxImg, setLightboxImg] = useState(null);
  const modalBodyRef = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (lightboxImg) {
          setLightboxImg(null);
        } else if (isOpen) {
          handleClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, lightboxImg, dontRemind]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    if (dontRemind) {
      localStorage.setItem("ahc_hide_user_guide", "true");
    } else {
      localStorage.removeItem("ahc_hide_user_guide");
    }
    if (onClose) onClose();
  };

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(`ug-sec-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="user-guide-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="user-guide-title"
    >
      <div className="user-guide-modal">
        {/* MODAL HEADER */}
        <header className="user-guide-header">
          <div className="user-guide-header-top">
            <div>
              <div className="user-guide-brand-wrap">
                <span className="user-guide-badge">Platform Documentation</span>
                <span className="user-guide-version">v1.0 • Official Guide</span>
              </div>
              <h2 id="user-guide-title" className="user-guide-title">
                AHC PHC Capacity Assessment Platform User Guide
              </h2>
              <p className="user-guide-subtitle">
                Step-by-step guidelines to create, score, manage, review, and report institutional primary healthcare capacity assessments.
              </p>
            </div>
            <button
              type="button"
              className="user-guide-close-btn"
              onClick={handleClose}
              aria-label="Close Guide"
              title="Close Guide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </header>

        {/* QUICK NAVIGATION TABS */}
        <nav className="user-guide-nav-bar" aria-label="User Guide Sections">
          {SECTIONS.map((sec) => (
            <button
              key={sec.id}
              type="button"
              className={`user-guide-nav-pill ${activeSection === sec.id ? "active" : ""}`}
              onClick={() => scrollToSection(sec.id)}
            >
              <span className="user-guide-nav-num">{sec.num}</span>
              {sec.title}
            </button>
          ))}
        </nav>

        {/* MODAL CONTENT BODY */}
        <div className="user-guide-body" ref={modalBodyRef}>
          {/* 1. INTRODUCTION */}
          <section id="ug-sec-intro" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">1</span>
              <h3 className="user-guide-section-title">Introduction</h3>
            </div>
            <p className="user-guide-section-desc">
              This guide explains how to use the <strong>AHC PHC Capacity Assessment Platform</strong> to create, complete, manage, and review institutional assessments.
            </p>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 1 <span>Login Page</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image1.png", caption: "Figure 1. Login Page" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image1.png", caption: "Figure 1. Login Page" })}
              >
                <img
                  src="/guide-images/image1.png"
                  alt="Figure 1. Login Page"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>

            <p className="user-guide-section-desc">
              Enter your username/email and password, then click <strong>Sign in</strong> to access the assessment portal.
            </p>

            <div className="user-guide-callout">
              <div className="user-guide-callout-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div className="user-guide-callout-text">
                <strong>Need an account?</strong> If you do not have an account, contact the system administrator to obtain login credentials before using the platform.
              </div>
            </div>
          </section>

          {/* 2. HOME DASHBOARD */}
          <section id="ug-sec-dashboard" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">2</span>
              <h3 className="user-guide-section-title">Home Dashboard</h3>
            </div>
            <p className="user-guide-section-desc">
              After logging in, you are taken to the <strong>Home dashboard</strong> where you can start a new assessment or access previous assessments.
            </p>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 2 <span>Home Dashboard</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image2.png", caption: "Figure 2. Home Dashboard" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image2.png", caption: "Figure 2. Home Dashboard" })}
              >
                <img
                  src="/guide-images/image2.png"
                  alt="Figure 2. Home Dashboard"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>
          </section>

          {/* 3. CREATING A NEW ASSESSMENT */}
          <section id="ug-sec-new-assessment" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">3</span>
              <h3 className="user-guide-section-title">Creating a New Assessment</h3>
            </div>
            <p className="user-guide-section-desc">
              Select <strong>New Assessment</strong> from the navigation menu. Complete the <strong>Institution Name</strong>, <strong>Generation Tier</strong>, <strong>Assessment Date</strong>, and <strong>Lead Assessor</strong> fields, then click <strong>Begin Assessment</strong>.
            </p>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 3 <span>Assessment Information</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image3.png", caption: "Figure 3. Assessment Information" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image3.png", caption: "Figure 3. Assessment Information" })}
              >
                <img
                  src="/guide-images/image3.png"
                  alt="Figure 3. Assessment Information"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>
          </section>

          {/* 4. COMPLETING THE ASSESSMENT */}
          <section id="ug-sec-completing" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">4</span>
              <h3 className="user-guide-section-title">Completing the Assessment</h3>
            </div>
            <p className="user-guide-section-desc">
              Use the navigation panel to move between the assessment sections. Each pillar contains domains and indicators that must be scored.
            </p>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 4 <span>Assessment Indicators</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image4.png", caption: "Figure 4. Assessment Indicators" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image4.png", caption: "Figure 4. Assessment Indicators" })}
              >
                <img
                  src="/guide-images/image4.png"
                  alt="Figure 4. Assessment Indicators"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>

            <p className="user-guide-section-desc">
              Click the <strong>Rubric</strong> button to review the scoring criteria. Select a score from <strong>1 to 4</strong> based on the evidence available.
            </p>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 5 <span>Selecting Scores and Recording Evidence</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image5.png", caption: "Figure 5. Selecting Scores and Recording Evidence" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image5.png", caption: "Figure 5. Selecting Scores and Recording Evidence" })}
              >
                <img
                  src="/guide-images/image5.png"
                  alt="Figure 5. Selecting Scores and Recording Evidence"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 6 <span>Saving a Draft or Completing an Assessment</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image6.png", caption: "Figure 6. Saving a Draft or Completing an Assessment" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image6.png", caption: "Figure 6. Saving a Draft or Completing an Assessment" })}
              >
                <img
                  src="/guide-images/image6.png"
                  alt="Figure 6. Saving a Draft or Completing an Assessment"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>

            <div className="user-guide-callout user-guide-callout-info">
              <div className="user-guide-callout-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
              </div>
              <div className="user-guide-callout-text">
                If you are not ready to finish the assessment, click <strong>Save Draft</strong>. Later, open the History page, locate the draft assessment, and choose <strong>Edit</strong> to continue where you left off. When all sections are complete, click <strong>Complete Assessment</strong> to finalize the assessment.
              </div>
            </div>
          </section>

          {/* 5. ASSESSMENT HISTORY */}
          <section id="ug-sec-history" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">5</span>
              <h3 className="user-guide-section-title">Assessment History</h3>
            </div>
            <p className="user-guide-section-desc">
              The <strong>History page</strong> displays all saved assessments. Use the search box or status filters to locate assessments quickly. From this page you can open, continue, or review completed assessments.
            </p>

            <figure className="user-guide-figure">
              <div className="user-guide-figure-header">
                <figcaption className="user-guide-figure-caption">
                  Figure 7 <span>Assessment History</span>
                </figcaption>
                <span
                  className="user-guide-zoom-hint"
                  onClick={() => setLightboxImg({ src: "/guide-images/image7.png", caption: "Figure 7. Assessment History" })}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                  Click to enlarge
                </span>
              </div>
              <div
                className="user-guide-img-container"
                onClick={() => setLightboxImg({ src: "/guide-images/image7.png", caption: "Figure 7. Assessment History" })}
              >
                <img
                  src="/guide-images/image7.png"
                  alt="Figure 7. Assessment History"
                  className="user-guide-img"
                  loading="lazy"
                />
                <div className="user-guide-img-overlay">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                  Preview Full Image
                </div>
              </div>
            </figure>
          </section>

          {/* 6. ADMIN PANEL */}
          <section id="ug-sec-admin" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">6</span>
              <h3 className="user-guide-section-title">Admin Panel</h3>
            </div>
            <p className="user-guide-section-desc">
                Users with the <strong>System Administrator</strong> or <strong>System Superadmin</strong> role have access to user management and assessment score review. Only superadmins can assign or remove the administrator role; administrators can manage assessor and reviewer accounts.
            </p>

            {/* 6.1 Creating New Users */}
            <div className="user-guide-subsection">
              <h4 className="user-guide-subsection-title">6.1 Creating New Users</h4>
              <p className="user-guide-section-desc">
                Select <strong>Users</strong> from the navigation menu to open the User Management page. This page is restricted to administrators and superadmins and is used to create accounts and control access to the platform.
              </p>

              <figure className="user-guide-figure">
                <div className="user-guide-figure-header">
                  <figcaption className="user-guide-figure-caption">
                    Figure 8 <span>User Management</span>
                  </figcaption>
                  <span
                    className="user-guide-zoom-hint"
                    onClick={() => setLightboxImg({ src: "/guide-images/image8.png", caption: "Figure 8. User Management" })}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                    Click to enlarge
                  </span>
                </div>
                <div
                  className="user-guide-img-container"
                  onClick={() => setLightboxImg({ src: "/guide-images/image8.png", caption: "Figure 8. User Management" })}
                >
                  <img
                    src="/guide-images/image8.png"
                    alt="Figure 8. User Management"
                    className="user-guide-img"
                    loading="lazy"
                  />
                  <div className="user-guide-img-overlay">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    Preview Full Image
                  </div>
                </div>
              </figure>

              <p className="user-guide-section-desc">
                To create a new account, fill in the <strong>Create User</strong> form on the left side of the page:
              </p>

              <div className="user-guide-fields-grid">
                <div className="user-guide-field-card">
                  <div className="user-guide-field-name">
                    Full Name <span className="user-guide-field-badge optional">Optional</span>
                  </div>
                  <div className="user-guide-field-desc">The display name shown across the portal and in assessment records.</div>
                </div>
                <div className="user-guide-field-card">
                  <div className="user-guide-field-name">
                    Username <span className="user-guide-field-badge">Required</span>
                  </div>
                  <div className="user-guide-field-desc">The unique identifier the user will sign in with.</div>
                </div>
                <div className="user-guide-field-card">
                  <div className="user-guide-field-name">
                    Email <span className="user-guide-field-badge">Required</span>
                  </div>
                  <div className="user-guide-field-desc">A valid email address associated with the account.</div>
                </div>
                <div className="user-guide-field-card">
                  <div className="user-guide-field-name">
                    Password <span className="user-guide-field-badge">Required</span>
                  </div>
                  <div className="user-guide-field-desc">A secure password for the user account.</div>
                </div>
                <div className="user-guide-field-card">
                  <div className="user-guide-field-name">
                    Role <span className="user-guide-field-badge">Required</span>
                  </div>
                  <div className="user-guide-field-desc">Assign account privileges as Admin, Coordinator, or Assessor.</div>
                </div>
              </div>

              <p className="user-guide-section-desc">
                Click <strong>Create User</strong> to save the account. New accounts appear immediately on the right side of the page under <strong>Existing Users</strong>, along with their username, email, role, and active status. Use the <strong>Refresh</strong> button to reload the list of existing users.
              </p>
            </div>

            {/* 6.2 Viewing Assessment Scores */}
            <div className="user-guide-subsection">
              <h4 className="user-guide-subsection-title">6.2 Viewing Assessment Scores</h4>
              <p className="user-guide-section-desc">
                Administrators can review assessment performance across the platform from the History page. In addition to the standard list of assessments, administrators see summary statistics at the top of the page.
              </p>

              <figure className="user-guide-figure">
                <div className="user-guide-figure-header">
                  <figcaption className="user-guide-figure-caption">
                    Figure 9 <span>Assessment History with Score Summary</span>
                  </figcaption>
                  <span
                    className="user-guide-zoom-hint"
                    onClick={() => setLightboxImg({ src: "/guide-images/image9.png", caption: "Figure 9. Assessment History with Score Summary" })}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                    Click to enlarge
                  </span>
                </div>
                <div
                  className="user-guide-img-container"
                  onClick={() => setLightboxImg({ src: "/guide-images/image9.png", caption: "Figure 9. Assessment History with Score Summary" })}
                >
                  <img
                    src="/guide-images/image9.png"
                    alt="Figure 9. Assessment History with Score Summary"
                    className="user-guide-img"
                    loading="lazy"
                  />
                  <div className="user-guide-img-overlay">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    Preview Full Image
                  </div>
                </div>
              </figure>

              <p className="user-guide-section-desc">
                These cards show the <strong>Total Assessments</strong> recorded, the number <strong>Completed</strong>, the <strong>Average Score</strong> across all completed assessments, and the number of institutions that reached <strong>Level A</strong>. Each assessment listed below the cards displays its own score out of 1,000 points and its resulting level (for example, Level B), giving administrators a quick way to gauge institutional performance without opening each assessment individually.
              </p>
            </div>
          </section>

          {/* 7. WRITING THE ASSESSMENT REPORT */}
          <section id="ug-sec-reports" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">7</span>
              <h3 className="user-guide-section-title">Writing the Assessment Report</h3>
            </div>
            <p className="user-guide-section-desc">
              Once an assessment is marked Complete, the scores and evidence recorded in the platform form the basis for the written assessment report. The Lead Assessor is responsible for turning this data into a clear, evidence-based report following the standards below.
            </p>

            <div className="user-guide-subsection">
              <h4 className="user-guide-subsection-title">7.1 Report Types</h4>
              <p className="user-guide-section-desc">
                Four types of report are produced from a completed assessment:
              </p>

              <div className="user-guide-reports-grid">
                <div className="user-guide-report-card">
                  <div className="user-guide-report-code">REPORT TYPE 1</div>
                  <h5 className="user-guide-report-title">Individual Institution Report (IIR)</h5>
                  <p className="user-guide-report-desc">
                    The full capacity profile for one institution, including scores, evidence, and recommendations. Produced once per institution per assessment cycle.
                  </p>
                </div>

                <div className="user-guide-report-card">
                  <div className="user-guide-report-code">REPORT TYPE 2</div>
                  <h5 className="user-guide-report-title">National Synthesis Report (NSR)</h5>
                  <p className="user-guide-report-desc">
                    A cross-institutional report summarizing patterns, strengths, and gaps across all assessed institutions. Produced once per cycle.
                  </p>
                </div>

                <div className="user-guide-report-card">
                  <div className="user-guide-report-code">REPORT TYPE 3</div>
                  <h5 className="user-guide-report-title">Executive Brief</h5>
                  <p className="user-guide-report-desc">
                    A short summary, four pages maximum of the National Synthesis Report, written for Ministers and senior policymakers.
                  </p>
                </div>

                <div className="user-guide-report-card">
                  <div className="user-guide-report-code">REPORT TYPE 4</div>
                  <h5 className="user-guide-report-title">Institutional Improvement Plan</h5>
                  <p className="user-guide-report-desc">
                    Completed by the institution itself in response to the recommendations documented in its Individual Institution Report.
                  </p>
                </div>
              </div>

              <figure className="user-guide-figure">
                <div className="user-guide-figure-header">
                  <figcaption className="user-guide-figure-caption">
                    Figure 10 <span>Assessment Report Builder</span>
                  </figcaption>
                  <span
                    className="user-guide-zoom-hint"
                    onClick={() => setLightboxImg({ src: "/guide-images/image10.png", caption: "Figure 10. Assessment Report Builder" })}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>
                    Click to enlarge
                  </span>
                </div>
                <div
                  className="user-guide-img-container"
                  onClick={() => setLightboxImg({ src: "/guide-images/image10.png", caption: "Figure 10. Assessment Report Builder" })}
                >
                  <img
                    src="/guide-images/image10.png"
                    alt="Figure 10. Assessment Report Builder"
                    className="user-guide-img"
                    loading="lazy"
                  />
                  <div className="user-guide-img-overlay">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    Preview Full Image
                  </div>
                </div>
              </figure>
            </div>
          </section>

          {/* 8. BEST PRACTICES */}
          <section id="ug-sec-best-practices" className="user-guide-section">
            <div className="user-guide-section-header">
              <span className="user-guide-section-number">8</span>
              <h3 className="user-guide-section-title">Best Practices</h3>
            </div>
            <p className="user-guide-section-desc">
              Follow these recommended practices to ensure high quality and consistency across all capacity assessments:
            </p>

            <div className="user-guide-practices-list">
              <div className="user-guide-practice-item">
                <div className="user-guide-practice-check">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="user-guide-practice-text">Save your work regularly.</div>
              </div>

              <div className="user-guide-practice-item">
                <div className="user-guide-practice-check">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="user-guide-practice-text">Review rubric descriptions before assigning scores.</div>
              </div>

              <div className="user-guide-practice-item">
                <div className="user-guide-practice-check">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="user-guide-practice-text">Provide evidence for each selected score.</div>
              </div>

              <div className="user-guide-practice-item">
                <div className="user-guide-practice-check">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="user-guide-practice-text">Verify assessment details before marking an assessment as complete.</div>
              </div>
            </div>
          </section>
        </div>

        {/* MODAL FOOTER */}
        <footer className="user-guide-footer">
          <label className="user-guide-remind-group">
            <input
              type="checkbox"
              className="user-guide-checkbox"
              checked={dontRemind}
              onChange={(e) => setDontRemind(e.target.checked)}
            />
            <span className="user-guide-remind-label">
              Don't remind me again
              <span className="user-guide-remind-subtext">
                You can always reopen this guide from the top navigation or user menu
              </span>
            </span>
          </label>

          <div className="user-guide-footer-actions">
            <button
              type="button"
              className="user-guide-btn-understand"
              onClick={handleClose}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              I Understand
            </button>
          </div>
        </footer>
      </div>

      {/* FULLSIZE IMAGE LIGHTBOX PREVIEW */}
      {lightboxImg && (
        <div
          className="user-guide-lightbox-backdrop"
          onClick={() => setLightboxImg(null)}
        >
          <button
            type="button"
            className="user-guide-lightbox-close"
            onClick={() => setLightboxImg(null)}
            aria-label="Close Preview"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <img
            src={lightboxImg.src}
            alt={lightboxImg.caption}
            className="user-guide-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="user-guide-lightbox-caption">{lightboxImg.caption}</div>
        </div>
      )}
    </div>
  );
}
